import { toast } from '@heroui/react'
import { create } from 'zustand'

import { reportsApi } from '@/api/reports.api'
import i18n from '@/i18n'
import { mapApiToUi } from '@/store/reports.store'
import type { Report as UiReport } from '@/types/report.types'
import type { VehicleAnalyticsTotals, VehicleCostEntry } from '@/types/vehicleAnalytics'
import { isFuelExpense } from '@/utils/fuelExpense'

interface VehicleAnalyticsFilters {
  vehicleID: string;
  from: string;
  to: string;
}

interface VehicleAnalyticsStore {
  reports: UiReport[];
  entries: VehicleCostEntry[];
  excludedAdvanceFuel: VehicleCostEntry[];
  totals: VehicleAnalyticsTotals;
  isLoading: boolean;
  error: string | null;
  filters: VehicleAnalyticsFilters;

  fetchAnalytics: () => Promise<void>;
  setFilters: (filters: Partial<VehicleAnalyticsFilters>) => void;
  resetFilters: () => void;
}

const EMPTY_TOTALS: VehicleAnalyticsTotals = {
  advance: 0,
  paidRoads: 0,
  fuel: 0,
  all: 0,
  fuelLiters: 0,
}

const EMPTY_FILTERS: VehicleAnalyticsFilters = {
  vehicleID: '',
  from: '',
  to: '',
}

function textOr(value: string | undefined, fallback?: string) {
  return value?.trim() ? value : fallback
}

function inRange(date: string, from: string, to: string) {
  const day = (date ?? '').slice(0, 10)
  return day >= from && day <= to
}

export function buildEntries(reports: UiReport[], from: string, to: string) {
  const entries: VehicleCostEntry[] = []
  const excludedAdvanceFuel: VehicleCostEntry[] = []
  // Paid roads and fuel are attached by BE by vehicle + report date range,
  // so reports with overlapping periods return the same rows. Count each once.
  const seenImported = new Set<string>()
  const isNewImported = (id: string) => {
    if (seenImported.has(id)) return false
    seenImported.add(id)
    return true
  }

  reports.forEach(report => {
    const base = {
      reportId: report.id,
      reportNumber: textOr(report.identifier, report.reportNumber) ?? '',
      driverId: report.driverId,
      driverName: textOr(report.driverName),
    }

    report.rows
      .filter(row => row.isIncluded !== false && inRange(row.date, from, to))
      .forEach(row => {
        const entry: VehicleCostEntry = {
          ...base,
          id: row.id,
          date: row.date.slice(0, 10),
          category: 'advance',
          amountPLN: row.amountPLN ?? 0,
          country: textOr(row.country),
          label: textOr(row.type, '—') ?? '—',
        }
        if (isFuelExpense(row.type)) {
          excludedAdvanceFuel.push(entry)
        } else {
          entries.push(entry)
        }
      })

    ;(report.paidRoads ?? [])
      .filter(road => inRange(road.date, from, to) && isNewImported(road.id))
      .forEach(road => entries.push({
        ...base,
        id: road.id,
        date: road.date.slice(0, 10),
        category: 'paidRoads',
        amountPLN: road.cost ?? 0,
        country: textOr(road.country),
        label: textOr(road.country, '—') ?? '—',
      }))

    ;(report.fuel ?? [])
      .filter(fuel => inRange(fuel.date, from, to) && isNewImported(fuel.id))
      .forEach(fuel => entries.push({
        ...base,
        id: fuel.id,
        date: fuel.date.slice(0, 10),
        category: 'fuel',
        amountPLN: fuel.cost ?? 0,
        country: textOr(fuel.country),
        label: fuel.type,
        liters: fuel.liters ?? 0,
        fuelType: fuel.type,
      }))
  })

  entries.sort((a, b) => a.date.localeCompare(b.date))
  excludedAdvanceFuel.sort((a, b) => a.date.localeCompare(b.date))

  const totals = entries.reduce<VehicleAnalyticsTotals>((acc, entry) => {
    acc[entry.category] += entry.amountPLN
    acc.all += entry.amountPLN
    acc.fuelLiters += entry.liters ?? 0
    return acc
  }, { ...EMPTY_TOTALS })

  return { entries, excludedAdvanceFuel, totals }
}

export const useVehicleAnalyticsStore = create<VehicleAnalyticsStore>((set, get) => ({
  reports: [],
  entries: [],
  excludedAdvanceFuel: [],
  totals: EMPTY_TOTALS,
  isLoading: false,
  error: null,
  filters: EMPTY_FILTERS,

  fetchAnalytics: async () => {
    const { filters } = get()
    if (!filters.vehicleID || !filters.from || !filters.to) {
      set({ reports: [], entries: [], excludedAdvanceFuel: [], totals: EMPTY_TOTALS, isLoading: false })
      return
    }

    set({ isLoading: true, error: null })

    try {
      const response = await reportsApi.getReports({
        vehicleID: filters.vehicleID,
        from: filters.from,
        to: filters.to,
        limit: 1000, // fetch all for the period
      })

      const summaryReports = response.Reports ?? []
      const fullReports = await Promise.all(
        summaryReports.map(r => reportsApi.getReport(r.ID))
      )

      if (get().filters !== filters) return

      const reports = fullReports.map(mapApiToUi)

      set({
        reports,
        ...buildEntries(reports, filters.from, filters.to),
        isLoading: false,
      })
    } catch (err) {
      if (get().filters !== filters) return
      const message = (err as Error).message
      set({ error: message, isLoading: false })
      toast.danger(i18n.t('reports.toast.fetch_error'), { description: message })
    }
  },

  setFilters: newFilters => {
    set(state => ({ filters: { ...state.filters, ...newFilters } }))
    get().fetchAnalytics()
  },

  resetFilters: () => {
    set({
      filters: EMPTY_FILTERS,
      reports: [],
      entries: [],
      excludedAdvanceFuel: [],
      totals: EMPTY_TOTALS,
      isLoading: false,
    })
  },
}))
